import type { BoardDoc } from '../doc/BoardDoc';
import { DEG, type Point, type Rect } from '../doc/geometry';
import type { Geometry, ImageItem, Item } from '../doc/schema';
import { safeKeyBetween } from '../doc/zorder';
import { enqueueUpload } from '../sync/uploadQueue';
import type { Ctx } from './actions';
import { getAssetBlob, getAssetMeta, storeImageAsset, type LocalAssetMeta } from './assets/localAssets';

/**
 * Cutouts: a piece of an image, outlined with a lasso, rectangle or ellipse, becomes a new
 * image item (transparent outside the outline) placed exactly over the part it was cut from,
 * so it can be dragged away like a scrap of paper. Optionally the source keeps a hole.
 * Everything is rendered in the browser from the original asset, and the result goes through
 * the normal import pipeline (variants, dedupe, upload), so afterwards it is an ordinary image.
 */

export type CutoutMode = 'lasso' | 'rect' | 'ellipse';

/** Soft-edge widths on offer, in world units at the image's on-board size. */
export const FEATHERS = [0, 2, 4, 8];

const ELLIPSE_SEGMENTS = 72;

/** The outline as a flat world polygon [x, y, ...]. */
export function cutoutPolygon(mode: CutoutMode, start: Point, end: Point, ring: number[]): number[] {
  if (mode === 'lasso') return ring;
  const x0 = Math.min(start.x, end.x);
  const y0 = Math.min(start.y, end.y);
  const x1 = Math.max(start.x, end.x);
  const y1 = Math.max(start.y, end.y);
  if (mode === 'rect') return [x0, y0, x1, y0, x1, y1, x0, y1];
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const rx = (x1 - x0) / 2;
  const ry = (y1 - y0) / 2;
  const out: number[] = [];
  for (let i = 0; i < ELLIPSE_SEGMENTS; i++) {
    const a = (i / ELLIPSE_SEGMENTS) * Math.PI * 2;
    out.push(cx + rx * Math.cos(a), cy + ry * Math.sin(a));
  }
  return out;
}

export function polygonBounds(poly: number[]): Rect | null {
  if (poly.length < 6) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i + 1 < poly.length; i += 2) {
    const x = poly[i]!;
    const y = poly[i + 1]!;
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** World point to the item's own unrotated space (origin at its top-left corner). */
export function worldToItemLocal(g: Geometry, p: Point): Point {
  const cx = g.x + g.w / 2;
  const cy = g.y + g.h / 2;
  const rad = -(g.rotation || 0) * DEG;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p.x - cx;
  const dy = p.y - cy;
  return { x: dx * cos - dy * sin + g.w / 2, y: dx * sin + dy * cos + g.h / 2 };
}

export function itemLocalToWorld(g: Geometry, p: Point): Point {
  const cx = g.x + g.w / 2;
  const cy = g.y + g.h / 2;
  const rad = (g.rotation || 0) * DEG;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p.x - g.w / 2;
  const dy = p.y - g.h / 2;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

const isImage = (it: Item): it is ImageItem => it.type === 'image';

function containsWorld(g: Geometry, world: Point): boolean {
  const l = worldToItemLocal(g, world);
  return l.x >= 0 && l.y >= 0 && l.x <= g.w && l.y <= g.h;
}

/**
 * The image to cut from at a world point: a selected image under the point wins (so a photo
 * can be picked out from under pieces already cut from it), else the topmost visible image.
 */
export function imageAt(board: BoardDoc, world: Point, selection: ReadonlySet<string> = new Set()): ImageItem | null {
  for (const id of selection) {
    const it = board.index.get(id);
    if (it && isImage(it) && !board.isHidden(it) && containsWorld(it, world)) return it;
  }
  const hits = board.index.query({ x: world.x - 0.5, y: world.y - 0.5, w: 1, h: 1 });
  for (let i = hits.length - 1; i >= 0; i--) {
    const it = hits[i]!;
    if (isImage(it) && !board.isHidden(it) && containsWorld(it, world)) return it;
  }
  return null;
}

/** Topmost visible image whose bounds meet the polygon's bounds (an outline that started off any image). */
export function imageUnderPolygon(board: BoardDoc, poly: number[]): ImageItem | null {
  const b = polygonBounds(poly);
  if (!b) return null;
  const hits = board.index.query(b);
  for (let i = hits.length - 1; i >= 0; i--) {
    const it = hits[i]!;
    if (isImage(it) && !board.isHidden(it)) return it;
  }
  return null;
}

// ---- rendering ----------------------------------------------------------------------------

interface Piece {
  file: File;
  /** Where the piece sits in the source item's local space (world units). */
  box: Rect;
}

async function loadOriginal(item: ImageItem): Promise<ImageBitmap | null> {
  const blob = await getAssetBlob(item.props.assetId, 'original');
  if (!blob) return null;
  try {
    return await createImageBitmap(blob, { imageOrientation: 'from-image' });
  } catch {
    return null;
  }
}

function pixelPath(local: number[], sx: number, sy: number): Path2D {
  const path = new Path2D();
  for (let i = 0; i + 1 < local.length; i += 2) {
    const x = local[i]! * sx;
    const y = local[i + 1]! * sy;
    if (i === 0) path.moveTo(x, y);
    else path.lineTo(x, y);
  }
  path.closePath();
  return path;
}

/** Fills the outline as a mask; a soft edge is a blur on the fill (skipped where the canvas has no filters, e.g. older Safari). */
function fillMask(ctx: CanvasRenderingContext2D, path: Path2D, featherPx: number): void {
  ctx.save();
  if (featherPx > 0.5 && 'filter' in ctx) ctx.filter = `blur(${(featherPx / 2).toFixed(2)}px)`;
  ctx.fillStyle = '#000';
  ctx.fill(path);
  ctx.restore();
}

/** Bounds of the pixels that are not (almost) fully transparent. */
function opaqueBounds(ctx: CanvasRenderingContext2D, w: number, h: number): Rect | null {
  const data = ctx.getImageData(0, 0, w, h).data;
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    const row = y * w * 4 + 3;
    for (let x = 0; x < w; x++) {
      if (data[row + x * 4]! > 2) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), type);
  });
}

async function baseName(item: ImageItem): Promise<string> {
  const meta = await getAssetMeta(item.props.assetId);
  return (meta?.name ?? 'image').replace(/\.[^./]*$/, '') || 'image';
}

/** Pixels per world unit along each axis (the box may not keep the image's aspect). */
function scaleOf(bmp: ImageBitmap, item: ImageItem): { sx: number; sy: number } {
  return { sx: bmp.width / Math.max(1, item.w), sy: bmp.height / Math.max(1, item.h) };
}

/** The piece: the image's pixels inside the outline, cropped to their bounds, transparent elsewhere. */
async function renderPiece(bmp: ImageBitmap, item: ImageItem, local: number[], featherWorld: number): Promise<Piece | null> {
  const { sx, sy } = scaleOf(bmp, item);
  const feather = (featherWorld * (sx + sy)) / 2;
  const b = polygonBounds(local);
  if (!b) return null;
  const x0 = Math.max(0, Math.floor(b.x * sx - feather));
  const y0 = Math.max(0, Math.floor(b.y * sy - feather));
  const x1 = Math.min(bmp.width, Math.ceil((b.x + b.w) * sx + feather));
  const y1 = Math.min(bmp.height, Math.ceil((b.y + b.h) * sy + feather));
  if (x1 - x0 < 1 || y1 - y0 < 1) return null;
  const canvas = document.createElement('canvas');
  canvas.width = x1 - x0;
  canvas.height = y1 - y0;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.translate(-x0, -y0);
  fillMask(ctx, pixelPath(local, sx, sy), feather);
  ctx.globalCompositeOperation = 'source-in';
  ctx.drawImage(bmp, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  const tight = opaqueBounds(ctx, canvas.width, canvas.height);
  if (!tight) return null;
  let out = canvas;
  if (tight.w !== canvas.width || tight.h !== canvas.height) {
    out = document.createElement('canvas');
    out.width = tight.w;
    out.height = tight.h;
    out.getContext('2d')?.drawImage(canvas, -tight.x, -tight.y);
  }
  const blob = await canvasToBlob(out, 'image/png');
  const name = `${await baseName(item)}-cutout.png`;
  return {
    file: new File([blob], name, { type: 'image/png' }),
    box: { x: (x0 + tight.x) / sx, y: (y0 + tight.y) / sy, w: tight.w / sx, h: tight.h / sy },
  };
}

/** The source with the outline punched out of it. */
async function renderHole(bmp: ImageBitmap, item: ImageItem, local: number[], featherWorld: number): Promise<File | null> {
  const { sx, sy } = scaleOf(bmp, item);
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(bmp, 0, 0);
  ctx.globalCompositeOperation = 'destination-out';
  fillMask(ctx, pixelPath(local, sx, sy), (featherWorld * (sx + sy)) / 2);
  const blob = await canvasToBlob(canvas, 'image/png');
  return new File([blob], `${await baseName(item)}-cut.png`, { type: 'image/png' });
}

// ---- the action ---------------------------------------------------------------------------

export interface CutoutOptions {
  /** Soft edge width, world units. */
  feather: number;
  /** Also remove the piece from the source image (a locked source is left alone). */
  punch: boolean;
}

export type CutoutResult = 'ok' | 'no-source' | 'empty' | 'unavailable';

export const CUTOUT_MESSAGES: Record<Exclude<CutoutResult, 'ok'>, string> = {
  'no-source': 'Draw on an image to cut a piece out',
  empty: 'Nothing of the image is inside the outline',
  unavailable: 'The image is not available offline yet',
};

/**
 * Cuts the world polygon out of the image and puts the piece on the board right above it
 * (same rotation and layer), selected. With `punch`, the source is re-rendered with a hole.
 */
export async function cutOut(ctx: Ctx, sourceId: string, worldPoly: number[], opts: CutoutOptions): Promise<CutoutResult> {
  const { board, pane } = ctx;
  const source = board.index.get(sourceId);
  if (!source || !isImage(source)) return 'no-source';
  const local: number[] = [];
  for (let i = 0; i + 1 < worldPoly.length; i += 2) {
    const p = worldToItemLocal(source, { x: worldPoly[i]!, y: worldPoly[i + 1]! });
    local.push(p.x, p.y);
  }
  const bmp = await loadOriginal(source);
  if (!bmp) return 'unavailable';
  try {
    const piece = await renderPiece(bmp, source, local, opts.feather);
    if (!piece) return 'empty';
    const maxSide = board.meta.get('importMaxSide');
    const importOpts = { maxSide: typeof maxSide === 'number' ? maxSide : 0 };
    const meta = await storeImageAsset(piece.file, board.boardId, importOpts);
    let hole: LocalAssetMeta | null = null;
    if (opts.punch && !board.isLocked(source)) {
      const f = await renderHole(bmp, source, local, opts.feather);
      if (f) hole = await storeImageAsset(f, board.boardId, importOpts);
    }
    // the source may have moved while rendering: place the piece relative to where it is now
    const g = board.index.get(sourceId);
    if (!g || !isImage(g)) return 'no-source';
    const centre = itemLocalToWorld(g, { x: piece.box.x + piece.box.w / 2, y: piece.box.y + piece.box.h / 2 });
    const all = board.index.all();
    const at = all.findIndex((it) => it.id === g.id);
    const z = safeKeyBetween(g.z, all[at + 1]?.z ?? null);
    let id = '';
    board.transact(() => {
      id = board.createItem({
        type: 'image',
        x: round(centre.x - piece.box.w / 2),
        y: round(centre.y - piece.box.h / 2),
        w: round(piece.box.w),
        h: round(piece.box.h),
        rotation: g.rotation,
        z,
        layerId: g.layerId,
        props: { assetId: meta.id, naturalW: meta.width, naturalH: meta.height, opacity: g.props.opacity },
      });
      if (hole) board.updateItem(g.id, { props: { assetId: hole.id, naturalW: hole.width, naturalH: hole.height } });
    });
    if (board.remote) {
      void enqueueUpload(board.boardId, meta.id);
      if (hole) void enqueueUpload(board.boardId, hole.id);
    }
    pane.getState().select([id]);
    return 'ok';
  } finally {
    bmp.close();
  }
}

const round = (v: number) => Math.round(v * 100) / 100;
